import type { CreatePassport, Passport, PublicPassport } from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

/** Cyber passports (task 76). */

const key = (t: string | undefined, id: string) => ['passports', t, id] as const;

export function usePassports(assessmentId: string | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: key(tenant?.id, assessmentId ?? ''),
    queryFn: () => api<Passport[]>('GET', `/assessments/${assessmentId}/passports`),
    enabled: !!tenant && !!assessmentId,
  });
}

export function usePassportActions(assessmentId: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => qc.invalidateQueries({ queryKey: key(tenant?.id, assessmentId) });
  return {
    create: useMutation({
      mutationFn: (body: CreatePassport) =>
        api<Passport>('POST', `/assessments/${assessmentId}/passports`, body),
      onSuccess: done,
    }),
    revoke: useMutation({
      mutationFn: (id: string) =>
        api<Passport>('POST', `/assessments/${assessmentId}/passports/${id}/revoke`),
      onSuccess: done,
    }),
  };
}

/** The link anyone can open, on this site. */
export const passportUrl = (token: string) => `${window.location.origin}/#/pass/${token}`;
export const passportDocumentUrl = (token: string, id: string) =>
  `/api/public/passports/${encodeURIComponent(token)}/documents/${id}`;

export async function fetchPublicPassport(token: string): Promise<PublicPassport | null> {
  const res = await fetch(`/api/public/passports/${encodeURIComponent(token)}`, { headers: { accept: 'application/json' } }); // prettier-ignore
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(res.status === 429 ? 'Too many requests. Try again in a minute.' : `The passport could not be loaded (${res.status}).`); // prettier-ignore
  return (await res.json()) as PublicPassport;
}
